import { MIN_VAULT_SCORE_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { cn } from '@/utils/cn'
import type { Progress } from '@/utils/getScoreRangeValue'
import { getScoreGradientBgClass } from '@/utils/scoreColors'
import type { PopoverProps } from '@radix-ui/react-popover'
import { AccordionItem } from '../common/accordion'
import { Label } from '../common/label'
import { Popover, PopoverContent, PopoverList, PopoverListItem } from '../common/popover'
import { RadioButton, RadioButtonGroup } from '../common/radio'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelectTrigger } from './filterSelect'

interface ReputationFilterProps {
  className?: string
}

const SCORE_OPTIONS: { label: string; minScore: number; range: Progress }[] = [
  { label: 'Any', minScore: 0, range: 1 },
  { label: '6+', minScore: 60, range: 2 },
  { label: '8+', minScore: 80, range: 3 },
]

function useScoreFilterState() {
  const { minVaultScore, updateSearchState, resetSearchParam } = useVaultsSearchState()
  const { trackEvent } = useMixpanel()

  const handleSelect = (value: string) => {
    const minScore = Number(value)
    if (minScore === 0) {
      resetSearchParam(MIN_VAULT_SCORE_KEY)
    } else {
      updateSearchState([MIN_VAULT_SCORE_KEY, minScore])
    }
    trackEvent('Filter used', { Filter: 'Score', Value: minScore === 0 ? 'Any' : `${minScore / 10}+` })
  }

  return { minVaultScore, handleSelect }
}

export function ReputationFilterAccordion() {
  const { minVaultScore, handleSelect } = useScoreFilterState()
  return (
    <AccordionItem value="score">
      <FilterAccordionTrigger name="score" />
      <FilterAccordionContent>
        <ScoreRadioList value={String(minVaultScore)} onValueChange={handleSelect} />
      </FilterAccordionContent>
    </AccordionItem>
  )
}

export function ReputationFilter({ className, modal }: ReputationFilterProps & Pick<PopoverProps, 'modal'>) {
  const { minVaultScore, handleSelect } = useScoreFilterState()
  const isDefault = minVaultScore === 0
  const selectedLabel = SCORE_OPTIONS.find((o) => o.minScore === minVaultScore)?.label ?? 'Any'

  return (
    <Popover modal={modal}>
      <FilterSelectTrigger className={cn('min-w-0', className)}>
        <div className="flex w-full max-w-full items-center gap-2 truncate">
          <span className={cn(!isDefault && 'text-accent')}>{isDefault ? 'Score' : `Score: ${selectedLabel}`}</span>
        </div>
      </FilterSelectTrigger>
      <PopoverContent className="w-auto">
        <ScoreRadioList value={String(minVaultScore)} onValueChange={handleSelect} />
      </PopoverContent>
    </Popover>
  )
}

function ScoreRadioList({ value, onValueChange }: { value: string; onValueChange: (v: string) => void }) {
  return (
    <RadioButtonGroup value={value} onValueChange={onValueChange} className="flex flex-col">
      <PopoverList>
        {SCORE_OPTIONS.map(({ label, minScore, range }) => (
          <PopoverListItem key={minScore} className="h-10 border-t first:border-none">
            <Label
              className={cn(
                'flex h-full w-full items-center justify-between gap-3 px-4',
                'hocus-within:bg-surface',
                value === String(minScore) && 'text-accent'
              )}
            >
              <div className="flex items-center gap-2">
                <ScoreDots range={range} />
                <div className="inline-flex">
                  <span>({label})</span>
                </div>
              </div>
              <RadioButton value={String(minScore)} />
            </Label>
          </PopoverListItem>
        ))}
      </PopoverList>
    </RadioButtonGroup>
  )
}

function ScoreDots({ range }: { range: Progress }) {
  const dotClass = getScoreGradientBgClass(range)
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className={cn('size-2 rounded-full', dotClass, i >= range && 'opacity-[0.33]')} />
      ))}
    </div>
  )
}
